import React from 'react';
import { Link } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import { Activity } from 'types/interfaces/activity';
import { formatAccessibleTime, formatRelativeTime } from 'utils/relativeTime';
import { ActivityUserGroup } from '../groupActivity';
import ActivityFeedItem from './ActivityFeedItem';
import { NewWantTag, TraderCollapse } from 'components/TraderListCollapse/TraderListCollapse';
import { TraderListCollapseApi } from 'hooks/useTraderListCollapse';
import { isNewWant, orderForCollapsed } from 'utils/traderListCollapse';
import './ActivityTradeGroup.css';

interface ActivityTradeGroupProps {
  group: ActivityUserGroup;
  /** CSS width (px) for each tile — driven by the S/M/L view mode (GROUPED_TILE_WIDTH). */
  tileWidth: number;
  onSend?: (activity: Activity) => void;
  /** Saved collapse state, shared with Send Cards. */
  collapse: TraderListCollapseApi;
}

/**
 * "By trader" view: every activity item from one user in a single section inside
 * the page's one glass plane (Asana 1219008751039721, decision 13). No glass of
 * its own: sections are separated by a hairline. The header shows the standard
 * identity block once; the tiles are the same card block as By card.
 *
 * A list longer than two rows can be collapsed to those two rows, newest wants
 * first (Asana 1219124305066425). The expanded view keeps the feed's order.
 */
export const ActivityTradeGroup: React.FC<ActivityTradeGroupProps> = ({ group, tileWidth, onSend, collapse }) => (
  <TraderCollapse
    traderId={group.user.id}
    username={group.user.username}
    total={group.activities.length}
    noun="wants"
    collapse={collapse}
    layoutKey={String(tileWidth)}
  >
    {(c) => (
  <section className="activity-group" data-testid="activity-group">
    <div className="activity-group-header">
      <Link className="activity-group-user" to={`/profile/u/${encodeURIComponent(group.user.username)}`}>
        <AvatarWithFlag
          avatarUrl={group.user.avatarUrl}
          username={group.user.username}
          size={34}
          countryCode={group.user.countryCode}
          isElite={group.user.isElite}
          showAnimatedBorder={group.user.showAnimatedBorder}
          borderColor1={group.user.borderColor1}
          borderColor2={group.user.borderColor2}
        />
        <span className="activity-group-user-info">
          <span className="activity-group-user-name">
            {group.user.username}
            {group.user.isElite && <EliteBadge isElite size={14} />}
          </span>
          {group.user.points != null && (
            <span className="activity-group-user-points">
              <Points points={group.user.points} type="small" />
            </span>
          )}
        </span>
      </Link>
      <span className="activity-group-meta">
        {group.count} {group.count === 1 ? 'card' : 'cards'} wanted ·{' '}
        <time dateTime={group.mostRecent} title={formatAccessibleTime(group.mostRecent)}>
          {formatRelativeTime(group.mostRecent)}
        </time>
      </span>
      {c.headerToggle}
    </div>

    <div
      className="activity-group-cards"
      ref={c.gridRef}
      style={{ '--tile': `${tileWidth}px` } as React.CSSProperties}
    >
      {c
        .limit(c.collapsed ? orderForCollapsed(group.activities, (a) => a.wantCreatedAt) : group.activities)
        .map((activity) => (
          <ActivityFeedItem
            key={activity.id}
            activity={activity}
            grouped
            onSend={onSend}
            imageOverlay={c.collapsed && isNewWant(activity.wantCreatedAt) ? <NewWantTag /> : undefined}
          />
        ))}
    </div>
    {c.footer}
  </section>
    )}
  </TraderCollapse>
);

export default ActivityTradeGroup;
